import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Skeleton from "react-loading-skeleton";
import { currentSearch, fetchAdminJson, LoadState } from "./adminApi";

type PurchaseLike = {
  id?: number;
  date?: unknown;
  purchase_date?: unknown;
  license_key?: string;
  license_email?: string;
  os?: string;
  browser?: string;
  browser_version?: string;
  language?: string;
};

type ActivationLike = {
  license_key?: string;
  date?: unknown;
  message?: string;
  result?: string;
  plugin_version?: string;
  xplane_version?: string;
  os?: string;
  ip_address?: string;
  mac_address?: string;
};

type AdminPurchasesProps = {
  purchases?: PurchaseLike[];
  activations?: ActivationLike[];
  activation_counts?: Record<string, number> | Map<string, number>;
  filter?: string;
  total?: number;
};

function asArray<T = any>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown, fallback = ""): string {
  if (value == null) {
    return fallback;
  }
  return String(value);
}

function asNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function formatDateTime(value: unknown): string {
  const parsed = parseDate(value);
  if (parsed != null) {
    return `${parsed.toISOString().replace("T", " ").slice(0, 16)}`;
  }

  const source = asString(value, "");
  if (source.length > 0) {
    return source.replace("T", " ").slice(0, 16);
  }
  return "-";
}

function classByBrowserOs(os: unknown): string {
  const normalized = asString(os).toLowerCase();
  if (normalized.includes("windows")) {
    return "os_win";
  }
  if (normalized.includes("macos")) {
    return "os_mac";
  }
  if (normalized.includes("linux")) {
    return "os_linux";
  }
  return "";
}

function colorByBrowser(browser: unknown): string {
  const normalized = asString(browser).toLowerCase();
  if (normalized.includes("firefox")) {
    return "#fdc";
  }
  if (normalized.includes("safari")) {
    return "#cfc";
  }
  if (normalized.includes("chrome")) {
    return "#cef";
  }
  return "#fff";
}

function colorByPluginOs(os: unknown): string {
  const normalized = asString(os);
  if (normalized.includes("Win")) {
    return "#cfc";
  }
  if (normalized.includes("OS X")) {
    if (normalized.includes("10.10")) {
      return "#cef";
    }
    if (normalized.includes("10.9")) {
      return "#ddf";
    }
    return "#ccf";
  }
  return "#fff";
}

function colorByXplaneVer(version: unknown): string {
  const normalized = asString(version);
  if (normalized.includes("102")) {
    return "#fdc";
  }
  if (normalized.includes("103")) {
    if (normalized.includes("10301")) {
      return "#cef";
    }
    if (normalized.includes("10300")) {
      return "#bde";
    }
    if (normalized.includes("10310")) {
      return "#ddf";
    }
    return "#ccf";
  }
  return "#fff";
}

function activationCountForKey(
  licenseKey: string,
  activationCounts?: Record<string, number> | Map<string, number>,
  activationsForKey: ActivationLike[] = []
): number {
  if (activationCounts instanceof Map) {
    return asNumber(activationCounts.get(licenseKey), activationsForKey.length);
  }
  if (activationCounts != null && typeof activationCounts === "object") {
    return asNumber((activationCounts as Record<string, number>)[licenseKey], activationsForKey.length);
  }
  return activationsForKey.length;
}

function buildActivationsByKey(activations: ActivationLike[]): Map<string, ActivationLike[]> {
  const map = new Map<string, ActivationLike[]>();
  activations.forEach((activation) => {
    const key = asString(activation.license_key);
    if (key.length === 0) {
      return;
    }
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)?.push(activation);
  });
  return map;
}

function activationSortKey(activation: ActivationLike): number {
  const parsed = parseDate(activation.date);
  return parsed == null ? 0 : parsed.getTime();
}

function purchaseSortKey(purchase: PurchaseLike): number {
  const parsed = parseDate(purchase.date ?? purchase.purchase_date);
  return parsed == null ? 0 : parsed.getTime();
}

function purchaseHeadClass(activationCount: number): string {
  if (activationCount === 0) {
    return "purchase_head not_activated";
  }
  if (activationCount > 5) {
    return "purchase_head high_activation_count";
  }
  return "purchase_head";
}

function ActivationListing(props: { activation: ActivationLike }) {
  const activation = props.activation;

  return (
    <div className="stats_purchases_activation">
      <div className="activation_time">{formatDateTime(activation.date)}</div>
      <div className="activation_block">
        {asString(activation.message)}
        <span style={{ float: "right" }}>{asString(activation.result)}</span>
        <br />
        <span>v{asString(activation.plugin_version)}</span> |{" "}
        <span style={{ backgroundColor: colorByXplaneVer(activation.xplane_version) }}>
          in X-plane {asString(activation.xplane_version)}
        </span>{" "}
        running on{" "}
        <span style={{ backgroundColor: colorByPluginOs(activation.os) }}>OS: {asString(activation.os)}</span>
        <div style={{ float: "right" }}>
          {asString(activation.ip_address)}@{asString(activation.mac_address)}
        </div>
      </div>
    </div>
  );
}

function PurchaseListing(props: {
  purchase: PurchaseLike;
  activations: ActivationLike[];
  activationCounts?: Record<string, number> | Map<string, number>;
}) {
  const purchase = props.purchase;
  const licenseKey = asString(purchase.license_key);
  const licenseEmail = asString(purchase.license_email);
  const purchaseId = asNumber(purchase.id, 0);
  const purchaseDate = formatDateTime(purchase.date ?? purchase.purchase_date);

  const sortedActivations = [...props.activations].sort((a, b) => activationSortKey(a) - activationSortKey(b));
  const activationCount = activationCountForKey(licenseKey, props.activationCounts, sortedActivations);
  const headClass = purchaseHeadClass(activationCount);
  const licenseDetailLink = `/admin/license_detail/${encodeURIComponent(licenseKey)}`;
  const validateLink = `/admin/key/decode/${encodeURIComponent(licenseKey)}/${encodeURIComponent(licenseEmail)}`;
  const changeEmailLink = purchaseId > 0 ? `/admin/purchase/${purchaseId}/change_email` : "#";

  return (
    <div className="purchase_box">
      <div className={headClass}>
        <a href={licenseDetailLink}>{licenseKey}</a> ({activationCount}) <a href={validateLink}>v</a>
        <span style={{ float: "right" }}>
          {licenseEmail}{" "}
          <a href={changeEmailLink} aria-disabled={purchaseId <= 0}>
            [change]
          </a>{" "}
          - {purchaseDate}
        </span>
        <br />
        <span style={{ fontSize: "small" }}>
          <span className={classByBrowserOs(purchase.os)}>{asString(purchase.os)}</span>
          <span
            style={{
              float: "right",
              backgroundColor: colorByBrowser(purchase.browser),
            }}
          >
            {asString(purchase.browser)} {asString(purchase.browser_version)}: {asString(purchase.language)}
          </span>
        </span>
      </div>

      {sortedActivations.map((activation, index) => (
        <ActivationListing
          key={`${licenseKey}-${asString(activation.date)}-${asString(activation.ip_address)}-${index}`}
          activation={activation}
        />
      ))}
    </div>
  );
}

export function AdminPurchases(props?: AdminPurchasesProps) {
  const [state, setState] = useState<LoadState<AdminPurchasesProps>>(
    props?.purchases == null ? { status: "loading" } : { status: "loaded", data: props }
  );

  useEffect(() => {
    if (props?.purchases != null) {
      return;
    }
    fetchAdminJson<AdminPurchasesProps>(`/purchases${currentSearch()}`)
      .then((data) => setState({ status: "loaded", data }))
      .catch((error: Error) => setState({ status: "error", error: error.message }));
  }, []);

  if (state.status === "error") {
    return <div className="alert alert-danger">{state.error}</div>;
  }

  const loadedProps = state.status === "loaded" ? state.data : props;
  const purchases = asArray<PurchaseLike>(loadedProps?.purchases);
  const activations = asArray<ActivationLike>(loadedProps?.activations);
  const activationsByKey = buildActivationsByKey(activations);
  const sortedPurchases = [...purchases].sort((a, b) => purchaseSortKey(b) - purchaseSortKey(a));
  const purchaseCount = loadedProps?.total ?? sortedPurchases.length;
  const filterValue = asString(loadedProps?.filter);

  const rows =
    state.status === "loading" ? (
      <Skeleton count={6} height={78} />
    ) : sortedPurchases.length === 0 ? (
      <div className="text-muted mt-3">No purchases found.</div>
    ) : (
      sortedPurchases.map((purchase, index) => {
        const licenseKey = asString(purchase.license_key);
        const purchaseActivations = activationsByKey.get(licenseKey) ?? [];
        return (
          <PurchaseListing
            key={`${licenseKey}-${asString(purchase.date ?? purchase.purchase_date)}-${index}`}
            purchase={purchase}
            activations={purchaseActivations}
            activationCounts={loadedProps?.activation_counts}
          />
        );
      })
    );

  return (
    <div className="container text-center">
      <div className="d-flex flex-column align-items-center py-2">
        <h2>Purchases ({purchaseCount})</h2>
        <Form method="get" action="/admin/purchases" className="admin_purchases_search">
          <InputGroup>
            <Form.Control name="filter" defaultValue={filterValue} placeholder="Search purchases" />
            <Button type="submit" variant="outline-primary">
              Search
            </Button>
          </InputGroup>
        </Form>
      </div>
      {rows}
    </div>
  );
}
